import { useEffect, useState } from 'react';
import { assetService } from '@/services/asset-service';

type ImagePreviewProps = {
  /** A local file to be previewed - this would be used for previewing files before upload */
  localFile?: File;
  /** The UUID of the IMAGE asset to be previewed. */
  assetId?: string;
  /** Optional alt text for the image */
  alt?: string;
};

export const ImagePreview = ({ 
  localFile, 
  assetId, 
  alt = "Asset preview"
}: ImagePreviewProps) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  useEffect(() => {
    const fetchImageAsset = async () => {
      if (assetId) {
        try {
          // Get the file URI from the asset
          const uri = await assetService.getAssetFileUri(assetId);
          setImageSrc(uri);
        } catch (error) {
          console.error('Error fetching image asset:', error);
          setImageSrc(null);
        }
      }
    };

    if (localFile) {
      // Create a temporary URL for the local file
      const url = URL.createObjectURL(localFile);
      setImageSrc(url);

      // Clean up the URL object when component unmounts or file changes
      return () => {
        URL.revokeObjectURL(url);
      };
    } else if (assetId) {
      fetchImageAsset();
    }
  }, [localFile, assetId]);

  if (!imageSrc) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-muted">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            {assetId ? "Image not available" : "Loading image..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex items-center justify-center p-2">
      <img 
        src={imageSrc} 
        alt={alt}
        className="max-w-full max-h-full object-contain"
      />
    </div>
  );
};